"use client";

import { type BlokProps, StoryblokComponentList } from "@trydig/storyblok";
import { useQueryState } from "nuqs";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { Typography } from "@/components/ui/Typography";
import { FAQ_TOPIC_PARAM } from "@/features/faq/config";
import type { FaqListingPageStoryblok } from "@/generated/sb/sb-types";

export const FaqListingPageBlok = ({ blok, preview }: BlokProps<FaqListingPageStoryblok>) => {
	const [activeSlug, setActiveSlug] = useQueryState(FAQ_TOPIC_PARAM);
	const groups = blok.groups ?? [];
	return (
		<BlokWrap editable={blok} as="main" className="bg-background-bg-sand-1">
			<section className="py-grid-section-vertical-md">
				<Container className="flex flex-col gap-grid-section-vertical-lg">
					<Typography variant="heading-display-md" as="h1" className="text-text-dark-text-1">
						{blok.title}
					</Typography>

					{groups.length > 1 ? (
						<div className="flex flex-wrap gap-grid-grid-gutter-md">
							{groups.map((group) => {
								const isActive = Boolean(group.title) && activeSlug === group.title;
								return (
									<Button
										key={group._uid}
										variant={isActive ? "solid" : "outline"}
										color="primary"
										size="large"
										aria-pressed={isActive}
										onClick={() => setActiveSlug(isActive ? null : (group.title ?? null))}
									>
										{group.title}
									</Button>
								);
							})}
						</div>
					) : null}

					<div className="h-px w-full bg-border-medium" />
				</Container>
			</section>

			{!!groups.length && (
				<section className="py-grid-section-vertical-lg">
					<Container className="grid grid-cols-12 gap-grid-grid-gutter-md">
						<div className="col-span-full flex flex-col gap-grid-section-vertical-lg lg:col-span-8">
							<StoryblokComponentList bloks={groups} preview={preview} />
						</div>
					</Container>
				</section>
			)}
		</BlokWrap>
	);
};

export const FaqListingPageBlokSkeleton = () => {
	return <main>FaqListingPageSkeleton</main>;
};
